<template>
  <BaseButton
    variant="primary"
    :class="['add-to-cart-btn d-block mt-24 mb-24', { loading: isLoading }]"
    data-testid="add-to-cart-btn"
    @click="onClick"
  >
    {{ $label('cart.addToCart') }}
  </BaseButton>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useUserStore } from '~/stores/user'
import { useShopCartStore } from '~/stores/shopCart'
import { TRACKING_LISTS } from '~/utils/constants'
import { useDrawer } from '~/composables/useDrawer'

export default {
  props: {
    book: {
      type: Object,
      required: true
    }
  },
  setup() {
    const { updateDrawerState } = useDrawer()

    return {
      updateDrawerState
    }
  },
  data() {
    return {
      isLoading: false
    }
  },
  computed: {
    ...mapState(useUserStore, ['loggedInUser']),
    ...mapState(useShopCartStore, ['cartData', 'cartLoaded']),
    itemAlreadyInCart() {
      return (
        this.cartData.items &&
        this.cartData.items.some((item) => item.itemUid === this.book.id)
      )
    }
  },
  methods: {
    ...mapActions(useShopCartStore, ['addToCart']),
    onClick() {
      if (this.isLoading) return

      if (!this.loggedInUser) {
        return this.openLoginDrawer()
      }

      if (!this.cartLoaded) return

      if (this.itemAlreadyInCart) {
        return this.handleDuplicateItem()
      }

      this.addItemToCart()
    },
    async addItemToCart() {
      try {
        this.isLoading = true

        await this.addToCart({
          cartUid: this.cartData.uid,
          productUid: this.book.id,
          price: this.book.salePrice
        })

        this.sendTracking()
        this.openCartDrawer()
      } catch (error) {
        const errorCode = this.$error.getErrorCode(error)

        if (errorCode === 'Orders.Cart.v3.CartItemBookNotOrderable') {
          this.$toast.error(this.$label('cart.checkout.bookNotOrderable'))
        } else if (errorCode === 'Orders.Cart.v3.DuplicateCartItem') {
          this.handleDuplicateItem()
        } else {
          this.$toast.error(this.$label('messages.errorOccuredMessage'))
          this.$sentryCaptureException(error)
        }
      } finally {
        this.isLoading = false
      }
    },
    handleDuplicateItem() {
      this.$toast.success(this.$label('cart.itemAlreadyInCartAlert'))
      this.openCartDrawer()
    },
    sendTracking() {
      const item = {
        id: this.book.id,
        name: this.book.title,
        price: this.book.listPrice,
        discount: this.book.listPrice - this.book.salePrice,
        ...TRACKING_LISTS.shop
      }

      this.$gtmTracking.addToCart(item, this.book.salePrice)
    },
    openLoginDrawer() {
      const loginDrawer = {
        menuPositionClass: 'right',
        title: 'user.signIn.title',
        component: 'base-login',
        props: {
          isHeadingShown: false,
          loginFormPrefix: 'login-',
          shouldPreventRedirect: true,
          inSlideMenu: true,
          onLoggedIn: this.addItemToCart,
          registerPath: '/shop/aanmelden'
        }
      }

      this.updateDrawerState({
        drawerOptions: loginDrawer,
        isDrawerShown: true
      })
    },
    openCartDrawer() {
      const cartDrawer = {
        menuPositionClass: 'right',
        title: 'cart.drawerTitle',
        component: 'cart-drawer',
        footer: 'cart-drawer-footer'
      }

      this.updateDrawerState({
        drawerOptions: cartDrawer,
        isDrawerShown: true
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@include media-breakpoint-down(sm) {
  .add-to-cart-btn {
    width: 100%;
  }
}
</style>
